// Which chat path a message takes: the SERVER-side agent run, or the
// browser-driven round loop in `chatRounds.js`.
//
// Pure and in its own module because it is the whole risk of flipping the
// default — `ChatPanel` cannot be rendered under jest, so a predicate left
// inline in it is untestable, and every clause below is a case where taking the
// wrong branch is a real bug rather than a preference.
//
// Server-side is the default for workflow chat because that is the point of the
// feature: the pipeline must keep running with the tab closed. The exclusions
// are the cases where it cannot or must not:
//
//   * `!isWorkflow` — a TILE chat is a conversation with one module's subagent,
//     which the user opened deliberately. A server run activates its own
//     subagents; typing into a tile is the interactive path and stays local.
//   * `readOnly` / `owner` — `/api/agent-run` is owner-only (no `?owner=`
//     parameter exists), so a grantee's send would 403. Read-only viewers also
//     must not mutate a canvas they cannot save.
//   * `tutorialActive` — a guided or server tutorial run is already driving this
//     workflow deterministically. Two supervisors on one graph is the one
//     combination that corrupts it.
//   * `!hasServerSend` — the host page did not wire the handler, so there is
//     nothing to call. Falling back keeps the panel usable rather than dead.
export function shouldUseServerRun({
  isWorkflow,
  readOnly,
  owner,
  tutorialActive,
  hasServerSend,
} = {}) {
  return !!isWorkflow && !readOnly && !owner && !tutorialActive && !!hasServerSend;
}

// True while a server agent run owns this chat's history. Used to skip the
// client's whole-list `saveHistory` PUT: the runner appends to the same history
// concurrently with DynamoDB `list_append`, so a PUT from here would clobber
// its narration — the identical hazard the server TUTORIAL run already guards.
export function serverRunOwnsHistory(agentRun) {
  return !!agentRun && !['done', 'failed', 'cancelled'].includes(agentRun.status);
}

// What a live run says before it has said anything. A server run has no SSE, so
// there is no token stream and nothing at all appears between sending a message and
// the first poll that carries text — which reads as the agent having ignored you.
export const THINKING = 'Thinking…';

// The placeholder shown in place of a streaming bubble. A server run has no SSE,
// so there is no token stream to render; `lastText` is the model's most recent
// text, which the status poll carries.
export function serverRunBubble(agentRun) {
  if (!agentRun) return THINKING;
  if (agentRun.status === 'awaiting_input') return agentRun.lastText || 'Waiting for your input.';
  if (agentRun.status === 'failed') return agentRun.error || 'The run stopped.';
  // TERMINAL states need their own answer. Without this a finished run with no
  // `lastText` fell through to "Working…" below — so the dashboard's activity list
  // showed a green checkmark captioned "Working…", which is a straight
  // contradiction. ChatPanel never saw it because it only renders this while
  // `serverRunOwnsHistory` (non-terminal) is true, so the bug only surfaced once a
  // second caller read it unconditionally.
  if (agentRun.status === 'done') return agentRun.lastText || 'Finished.';
  if (agentRun.status === 'cancelled') return agentRun.lastText || 'Stopped.';
  const queued = agentRun.queuedTurns || 0;
  const busy = (agentRun.pendingJobs || []).length;
  // HELD. Its own branch and not the "Working…" line below, because the two counts
  // mean the opposite thing here: queued steps are what the hold is HOLDING, not work
  // about to happen, while a computing module genuinely is still going (a pause does
  // not kill a solve in flight). Reported as such, so "paused" and "paused but a solve
  // is still running" are distinguishable — otherwise a user who paused mid-solve sees
  // a stopped run and a machine still burning.
  if (agentRun.status === 'paused') {
    const held = [];
    if (busy) held.push(`${busy} module${busy === 1 ? '' : 's'} still computing`);
    if (queued) held.push(`${queued} step${queued === 1 ? '' : 's'} held`);
    const detail = held.length ? ` (${held.join(', ')})` : '';
    return `${agentRun.lastText || 'Paused.'}${detail}`;
  }
  const parts = [];
  if (busy) parts.push(`${busy} module${busy === 1 ? '' : 's'} computing`);
  if (queued) parts.push(`${queued} step${queued === 1 ? '' : 's'} queued`);
  const suffix = parts.length ? ` (${parts.join(', ')})` : '';
  // "Thinking…" only when there is genuinely nothing else to report — the opening
  // moments of a run, before the first round has produced any text. With work in
  // flight "Working…" is the accurate word, and it carries the counts.
  const idle = !agentRun.lastText && !busy && !queued;
  return `${agentRun.lastText || (idle ? THINKING : 'Working…')}${suffix}`;
}
